import { DatePipe } from '@angular/common';
import { Component, computed, inject, input, OnInit, output, signal } from '@angular/core';
import {
  AdminButtonComponent,
  AdminEmptyStateComponent,
  AdminModalComponent,
  AdminTableComponent,
  AdminToastService,
} from '../../admin';
import type {
  LeadDuplicateAction,
  LeadDuplicateGroup,
  LeadDuplicatesArea,
  LeadDuplicatesPreview,
} from '../../models/lead-duplicates.model';
import { LeadsService } from '../../services/leads.service';
import { TenantLeadsService } from '../../services/tenant-leads.service';

@Component({
  selector: 'app-lead-duplicates-modal',
  standalone: true,
  imports: [DatePipe, AdminModalComponent, AdminButtonComponent, AdminTableComponent, AdminEmptyStateComponent],
  templateUrl: './lead-duplicates-modal.component.html',
  styleUrl: './lead-duplicates-modal.component.scss',
})
export class LeadDuplicatesModalComponent implements OnInit {
  private readonly leadsService = inject(LeadsService);
  private readonly tenantLeadsService = inject(TenantLeadsService);
  private readonly toast = inject(AdminToastService);

  readonly area = input.required<LeadDuplicatesArea>();
  readonly closed = output<void>();
  readonly removed = output<void>();
  readonly mergeRequested = output<string[]>();

  readonly loading = signal(true);
  readonly removing = signal(false);
  readonly error = signal<string | null>(null);
  readonly preview = signal<LeadDuplicatesPreview | null>(null);

  readonly totalToDelete = computed(() => this.countActions('delete'));
  readonly canConfirm = computed(() => {
    const data = this.preview();
    if (!data || this.totalToDelete() === 0) return false;
    return data.groups.every((group) => group.leads.some((lead) => lead.action === 'keep'));
  });

  ngOnInit(): void {
    this.loadPreview();
  }

  close(): void {
    this.closed.emit();
  }

  loadPreview(): void {
    this.loading.set(true);
    this.error.set(null);
    const request =
      this.area() === 'tenant'
        ? this.tenantLeadsService.previewDuplicates()
        : this.leadsService.previewDuplicates();

    request.subscribe({
      next: (data) => {
        this.preview.set(data);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.error?.message ?? 'Could not load duplicate leads.');
        this.loading.set(false);
      },
    });
  }

  setAction(leadId: string, action: LeadDuplicateAction): void {
    const data = this.preview();
    if (!data) return;

    const group = data.groups.find((item) => item.leads.some((lead) => lead._id === leadId));
    if (!group) return;

    if (action === 'delete') {
      const otherKeeps = group.leads.filter(
        (lead) => lead._id !== leadId && lead.action === 'keep',
      ).length;
      if (otherKeeps === 0) {
        this.toast.error('Each name group must keep at least one lead.');
        return;
      }
    }

    const groups = data.groups.map((item) => ({
      ...item,
      leads: item.leads.map((lead) => (lead._id === leadId ? { ...lead, action } : lead)),
    }));

    this.preview.set({
      ...data,
      groups,
      totalToDelete: groups.flatMap((item) => item.leads).filter((lead) => lead.action === 'delete')
        .length,
    });
  }

  confirmRemove(): void {
    const current = this.preview();
    if (!current || !this.canConfirm() || this.removing()) return;

    const leadIds = current.groups
      .flatMap((group) => group.leads)
      .filter((lead) => lead.action === 'delete')
      .map((lead) => lead._id);

    this.removing.set(true);
    this.error.set(null);

    const request =
      this.area() === 'tenant'
        ? this.tenantLeadsService.removeDuplicates(leadIds)
        : this.leadsService.removeDuplicates(leadIds);

    request.subscribe({
      next: (result) => {
        this.removing.set(false);
        if (result.deletedCount === 0) {
          this.toast.success('No duplicate leads to remove.');
        } else {
          this.toast.success(`Removed ${result.deletedCount} duplicate lead${result.deletedCount === 1 ? '' : 's'}.`);
        }
        this.removed.emit();
        this.close();
      },
      error: (err) => {
        this.removing.set(false);
        this.error.set(err?.error?.message ?? 'Could not remove duplicate leads.');
      },
    });
  }

  flatRows(): { group: LeadDuplicateGroup; lead: LeadDuplicateGroup['leads'][number] }[] {
    const current = this.preview();
    if (!current) return [];
    return current.groups.flatMap((group) => group.leads.map((lead) => ({ group, lead })));
  }

  requestMerge(group: LeadDuplicateGroup): void {
    if (group.leads.length < 2) return;
    this.mergeRequested.emit(group.leads.map((lead) => lead._id));
  }

  private countActions(action: LeadDuplicateAction): number {
    const data = this.preview();
    if (!data) return 0;
    return data.groups.flatMap((group) => group.leads).filter((lead) => lead.action === action).length;
  }
}
